import { Entity } from '@api/api/entity-schema';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { EllipsisDetector } from '@terminal/components/ellipsis-detector/EllipsisDetector';
import clsx from 'clsx';
import { format } from 'date-fns';
import { Trans, useTranslation } from 'react-i18next';
import { PulseInsightCategory, PulseSentimentInsight } from '../../pulse-types';
import styles from './CategoryPill.module.scss';

export const CATEGORY_CLASS: Record<PulseInsightCategory, string> = {
  market_catalyst: styles.marketCatalyst,
  company_catalyst: styles.companyCatalyst,
  earnings_recap: styles.earningsRecap,
  scenario_insight: styles.scenarioInsight,
};

interface CategoryPillProps {
  insight: Pick<PulseSentimentInsight, 'category' | 'date' | 'company'>;
  companyEntity?: Entity;
}

export const CategoryPill = ({ insight, companyEntity }: CategoryPillProps) => {
  const { t } = useTranslation(['widget']);

  const companyName =
    companyEntity?.name_short || companyEntity?.name || insight.company?.name;
  const pillLabels = {
    category: t(
      `widget:themeOverview.pulse.drawer.category_${insight.category}`
    ),
    date: format(new Date(insight.date), 'd MMM'),
  };

  const renderAssetLogo = () => {
    return (
      companyEntity && <AssetLogoContainer entity={companyEntity} size={20} />
    );
  };

  const renderPillLabelContent = () => {
    return (
      <span className={styles.categoryPillLabel}>
        {companyName ? (
          <Trans
            t={t}
            i18nKey="widget:themeOverview.pulse.drawer.pill_companyCategoryDate"
            components={[<span key="key-0" className={styles.pillCompany} />]}
            values={{
              company: companyName,
              ...pillLabels,
            }}
          />
        ) : (
          t('widget:themeOverview.pulse.drawer.pill_categoryDate', {
            ...pillLabels,
          })
        )}
      </span>
    );
  };

  const renderPillLabel = () => {
    return (
      <span className={styles.tooltipContent}>
        {renderAssetLogo()}
        {renderPillLabelContent()}
      </span>
    );
  };

  return (
    <span
      className={clsx(styles.categoryPill, CATEGORY_CLASS[insight.category])}
    >
      {renderAssetLogo()}
      <EllipsisDetector inPortal label={renderPillLabel()}>
        {renderPillLabelContent()}
      </EllipsisDetector>
    </span>
  );
};
